import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { logger } from "../../utils/logger";
import AuthLayout from "../../layouts/AuthLayout";
import { useRedirectIfAuthenticated } from "../../hooks/useRedirectIfAuthenticated";
import PasswordCriteria from "../../components/auth/PasswordCriteria";
import { validatePasswordStrength } from "../../utils/password-policy";

type Step = "request" | "confirm";

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  useRedirectIfAuthenticated();

  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);


    const username = email.trim();
    if (!username) {
      setError("Please enter your email.");
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword({ username });
      setStep("confirm");
      setInfo("We have sent a reset code to your email.");
    } catch (err: any) {
      logger.error("Reset password error", err instanceof Error ? err : undefined);
      const message =
        err?.message ||
        "We could not start the reset process. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Check strength here, not only on sign-up. Cognito enforces the same
    // policy on a reset, so without this the user sees a raw
    // InvalidPasswordException instead of which rule they missed.
    const weakness = validatePasswordStrength(newPassword);
    if (weakness) {
      setError(weakness);
      return;
    }

    const username = email.trim();

    setIsSubmitting(true);
    try {
      await confirmResetPassword({username,
        confirmationCode: code.trim(),
        newPassword,
      });

      setInfo("Password updated. Redirecting to login.");
      setTimeout(
        () =>
          navigate("/login", {
            state: { passwordResetSuccess: true, email: email.trim().toLowerCase() },
          }),
        800
      );
    } catch (err: any) {
      logger.error("Confirm reset error", err instanceof Error ? err : undefined);
      const message =
        err?.message || "We could not reset your password. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToLogin = () => navigate("/login");

  const subtitle =
    step === "request"
      ? "Enter your email and we will send you a password reset code."
      : "Enter the code from your email and choose a new password.";

  return (
    <AuthLayout
      title="Forgot password"
      subtitle={subtitle}
      heroTitleLine1="Reset password"
      heroTitleLine2="Recover access to your EMR"
    >
      {step === "request" ? (
        <form onSubmit={handleRequestCode} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Email
            </label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Enter your email"
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </p>
          )}
          {info && (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
              {info}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-1 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Sending..." : "Send reset code"}
          </button>

          <div className="pt-4 text-sm text-slate-500">
            Remember your password?
            <button
              type="button"
              onClick={goToLogin}
              className="ml-1 font-medium text-[#0066cc] hover:underline text-sm"
            >
              Back to login
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleConfirmReset} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Email
            </label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type="email"
                value={email}
                disabled
                className="w-full border-none bg-transparent text-base text-slate-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600">
              Reset code
            </label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type="text"
                required
                value={code}
                onChange={e => setCode(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Enter the code"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600">
              New password
            </label>
            <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type={showNewPassword ? "text" : "password"}
                required
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(v => !v)}
                className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600"
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                {showNewPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <PasswordCriteria password={newPassword} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600">
              Confirm password
            </label>
            <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type={showConfirmPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Re enter new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(v => !v)}
                className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </p>
          )}
          {info && (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
              {info}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-1 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Updating..." : "Update password"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
